import { Link as RouterLink, useLocation } from 'react-router-dom'
import { Box, VStack, HStack, Text, Badge, Separator, Portal } from '@chakra-ui/react'
import { Tooltip } from '@chakra-ui/react'
import { MenuRoot, MenuTrigger, MenuContent, MenuItem, MenuPositioner } from '@chakra-ui/react'
import { Settings, FileSearch, PanelLeft } from 'lucide-react'
import { AgencyLogo } from '@/features/agencies/components/AgencyLogo'
import { useAuth } from '@/features/auth/hooks/useAuth'
import { useAgency } from '@/features/agencies/hooks/useAgency'
import type { LucideIcon } from 'lucide-react'
import { useState } from 'react'

interface NavigationItem {
  path: string
  label: string
  icon: LucideIcon
  badge?: string
}

interface CollapsibleMainSidebarProps {
  navigationItems: NavigationItem[]
}

type SidebarMode = 'hover' | 'expanded' | 'collapsed'

export function CollapsibleMainSidebar({ navigationItems }: CollapsibleMainSidebarProps) {
  const { data: auth } = useAuth()
  const { data: agency } = useAgency()
  const location = useLocation()
  const [sidebarMode, setSidebarMode] = useState<SidebarMode>('hover')

  const isActiveRoute = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(path + '/')
  }

  const isAdminOrManager = auth?.isAdmin || auth?.residences.some((r) => r.role === 'MANAGER')

  const getWidth = () => {
    if (sidebarMode === 'expanded') return '240px'
    if (sidebarMode === 'collapsed') return '56px'
    return '56px' // hover mode default
  }

  const shouldExpand = sidebarMode === 'expanded'

  return (
    <Box
      w={getWidth()}
      minW={getWidth()}
      flexShrink={0}
      bg="sidebar.bg"
      color="gray.800"
      py={3}
      position="sticky"
      top={0}
      h="100vh"
      borderRight="1px solid"
      borderColor="gray.200"
      boxShadow="sm"
      transition="width 0.2s ease-in-out, min-width 0.2s ease-in-out"
      _hover={
        sidebarMode === 'hover'
          ? {
              w: '240px',
              minW: '240px',
            }
          : {}
      }
      className="sidebar-container"
      data-sidebar-mode={sidebarMode}
      overflow="hidden"
      overflowX="hidden"
    >
      {/* Logo */}
      <Box
        mb={4}
        h="48px"
        px={2}
        display="flex"
        alignItems="center"
      >
        <Box w="40px" h="40px" display="flex" alignItems="center" justifyContent="center" flexShrink={0}>
          <AgencyLogo variant="compact" height="32px" />
        </Box>
        <Box
          ml={2}
          display={shouldExpand ? 'block' : 'none'}
          css={{
            '.sidebar-container[data-sidebar-mode="hover"]:hover &': {
              display: 'block',
            },
          }}
        >
          <Text fontSize="sm" fontWeight="bold" letterSpacing="tight" color="gray.800" whiteSpace="nowrap">
            {agency?.name || 'Agence'}
          </Text>
          <Text fontSize="xs" color="gray.600" whiteSpace="nowrap">
            Outil de gestion
          </Text>
        </Box>
      </Box>

      {/* Navigation Items */}
      <VStack align="stretch" gap={0.5} px={2} flex={1} overflowY="auto" overflowX="hidden" pb="60px">
        {/* Dashboard (first item) */}
        {navigationItems.slice(0, 1).map((item) => {
          const isActive = isActiveRoute(item.path)
          const IconComponent = item.icon
          return (
            <Tooltip.Root key={item.path} positioning={{ placement: 'right' }}>
              <Tooltip.Trigger asChild>
                <RouterLink to={item.path}>
                  <HStack
                    gap={2}
                    h="40px"
                    px={2.5}
                    borderRadius="sm"
                    fontWeight={isActive ? 'semibold' : 'normal'}
                    bg={isActive ? 'brand.500' : 'transparent'}
                    color={isActive ? 'white' : 'gray.700'}
                    cursor="pointer"
                    _hover={{
                      bg: isActive ? 'brand.600' : 'gray.100',
                      color: isActive ? 'white' : 'gray.900',
                    }}
                    transition="all 0.2s"
                  >
                    <Box w="20px" h="20px" display="flex" alignItems="center" justifyContent="center" flexShrink={0}>
                      <IconComponent size={20} />
                    </Box>
                    <HStack
                      flex={1}
                      gap={2}
                      display={shouldExpand ? 'flex' : 'none'}
                      css={{
                        '.sidebar-container[data-sidebar-mode="hover"]:hover &': {
                          display: 'flex',
                        },
                      }}
                    >
                      <Text fontSize="sm" whiteSpace="nowrap">
                        {item.label}
                      </Text>
                      {item.badge && (
                        <Badge
                          size="xs"
                          variant="solid"
                          colorPalette={item.badge === 'WIP' ? 'orange' : 'gray'}
                          fontSize="9px"
                          px={1.5}
                        >
                          {item.badge}
                        </Badge>
                      )}
                    </HStack>
                  </HStack>
                </RouterLink>
              </Tooltip.Trigger>
              <Tooltip.Positioner
                css={{
                  '.sidebar-container[data-sidebar-mode="hover"]:hover &': {
                    display: 'none',
                  },
                }}
              >
                <Tooltip.Content>{item.label}</Tooltip.Content>
              </Tooltip.Positioner>
            </Tooltip.Root>
          )
        })}

        {/* Separator */}
        <Separator my={2} />

        {/* Rest of navigation items */}
        {navigationItems.slice(1).map((item) => {
          const isActive = isActiveRoute(item.path)
          const IconComponent = item.icon
          return (
            <Tooltip.Root key={item.path} positioning={{ placement: 'right' }}>
              <Tooltip.Trigger asChild>
                <RouterLink to={item.path}>
                  <HStack
                    gap={2}
                    h="40px"
                    px={2.5}
                    borderRadius="sm"
                    fontWeight={isActive ? 'semibold' : 'normal'}
                    bg={isActive ? 'brand.500' : 'transparent'}
                    color={isActive ? 'white' : 'gray.700'}
                    cursor="pointer"
                    _hover={{
                      bg: isActive ? 'brand.600' : 'gray.100',
                      color: isActive ? 'white' : 'gray.900',
                    }}
                    transition="all 0.2s"
                  >
                    <Box w="20px" h="20px" display="flex" alignItems="center" justifyContent="center" flexShrink={0}>
                      <IconComponent size={20} />
                    </Box>
                    <HStack
                      flex={1}
                      gap={2}
                      display={shouldExpand ? 'flex' : 'none'}
                      css={{
                        '.sidebar-container[data-sidebar-mode="hover"]:hover &': {
                          display: 'flex',
                        },
                      }}
                    >
                      <Text fontSize="sm" whiteSpace="nowrap">
                        {item.label}
                      </Text>
                      {item.badge && (
                        <Badge
                          size="xs"
                          variant="solid"
                          colorPalette={item.badge === 'WIP' ? 'orange' : 'gray'}
                          fontSize="9px"
                          px={1.5}
                        >
                          {item.badge}
                        </Badge>
                      )}
                    </HStack>
                  </HStack>
                </RouterLink>
              </Tooltip.Trigger>
              <Tooltip.Positioner
                css={{
                  '.sidebar-container[data-sidebar-mode="hover"]:hover &': {
                    display: 'none',
                  },
                }}
              >
                <Tooltip.Content>{item.label}</Tooltip.Content>
              </Tooltip.Positioner>
            </Tooltip.Root>
          )
        })}

        {/* Separator before admin section */}
        {isAdminOrManager && <Separator my={2} />}

        {/* Administration */}
        {isAdminOrManager && (
          <Tooltip.Root positioning={{ placement: 'right' }}>
            <Tooltip.Trigger asChild>
              <RouterLink to="/app/settings/agency">
                <HStack
                  gap={2}
                  h="40px"
                  px={2.5}
                  borderRadius="sm"
                  fontWeight={isActiveRoute('/app/settings') ? 'semibold' : 'normal'}
                  bg={isActiveRoute('/app/settings') ? 'brand.500' : 'transparent'}
                  color={isActiveRoute('/app/settings') ? 'white' : 'gray.700'}
                  cursor="pointer"
                  _hover={{
                    bg: isActiveRoute('/app/settings') ? 'brand.600' : 'gray.100',
                    color: isActiveRoute('/app/settings') ? 'white' : 'gray.900',
                  }}
                  transition="all 0.2s"
                >
                  <Box w="20px" h="20px" display="flex" alignItems="center" justifyContent="center" flexShrink={0}>
                    <Settings size={20} />
                  </Box>
                  <Text
                    fontSize="sm"
                    whiteSpace="nowrap"
                    display={shouldExpand ? 'block' : 'none'}
                    css={{
                      '.sidebar-container[data-sidebar-mode="hover"]:hover &': {
                        display: 'block',
                      },
                    }}
                  >
                    Administration
                  </Text>
                </HStack>
              </RouterLink>
            </Tooltip.Trigger>
            <Tooltip.Positioner
              css={{
                '.sidebar-container:hover &': {
                  display: 'none',
                },
              }}
            >
              <Tooltip.Content>Administration</Tooltip.Content>
            </Tooltip.Positioner>
          </Tooltip.Root>
        )}

        {/* Audit Log */}
        {auth?.isAdmin && (
          <Tooltip.Root positioning={{ placement: 'right' }}>
            <Tooltip.Trigger asChild>
              <RouterLink to="/app/admin/audit-logs">
                <HStack
                  gap={2}
                  h="40px"
                  px={2.5}
                  borderRadius="sm"
                  fontWeight={isActiveRoute('/app/admin/audit-logs') ? 'semibold' : 'normal'}
                  bg={isActiveRoute('/app/admin/audit-logs') ? 'brand.500' : 'transparent'}
                  color={isActiveRoute('/app/admin/audit-logs') ? 'white' : 'gray.700'}
                  cursor="pointer"
                  _hover={{
                    bg: isActiveRoute('/app/admin/audit-logs') ? 'brand.600' : 'gray.100',
                    color: isActiveRoute('/app/admin/audit-logs') ? 'white' : 'gray.900',
                  }}
                  transition="all 0.2s"
                >
                  <Box w="20px" h="20px" display="flex" alignItems="center" justifyContent="center" flexShrink={0}>
                    <FileSearch size={20} />
                  </Box>
                  <Text
                    fontSize="sm"
                    whiteSpace="nowrap"
                    display={shouldExpand ? 'block' : 'none'}
                    css={{
                      '.sidebar-container[data-sidebar-mode="hover"]:hover &': {
                        display: 'block',
                      },
                    }}
                  >
                    Journal d'audit
                  </Text>
                </HStack>
              </RouterLink>
            </Tooltip.Trigger>
            <Tooltip.Positioner
              css={{
                '.sidebar-container:hover &': {
                  display: 'none',
                },
              }}
            >
              <Tooltip.Content>Journal d'audit</Tooltip.Content>
            </Tooltip.Positioner>
          </Tooltip.Root>
        )}
      </VStack>

      {/* Sidebar Mode Selector */}
      <Box position="absolute" bottom={2} left={0} right={0} px={2}>
        <MenuRoot positioning={{ placement: 'top-start' }} lazyMount unmountOnExit>
          <MenuTrigger asChild>
            <HStack
              gap={2}
              h="40px"
              px={2.5}
              borderRadius="sm"
              cursor="pointer"
              bg="transparent"
              color="gray.700"
              _hover={{
                bg: 'gray.100',
                color: 'gray.900',
              }}
              transition="all 0.2s"
              title={!shouldExpand && sidebarMode === 'hover' ? 'Sidebar control' : undefined}
            >
              <Box w="20px" h="20px" display="flex" alignItems="center" justifyContent="center" flexShrink={0}>
                <PanelLeft size={20} />
              </Box>
              <Text
                fontSize="sm"
                whiteSpace="nowrap"
                display={shouldExpand ? 'block' : 'none'}
                css={{
                  '.sidebar-container[data-sidebar-mode="hover"]:hover &': {
                    display: 'block',
                  },
                }}
              >
                Sidebar control
              </Text>
            </HStack>
          </MenuTrigger>
          <Portal>
            <MenuPositioner>
              <MenuContent w="200px" maxW="200px">
                <MenuItem
                  value="hover"
                  onClick={() => setSidebarMode('hover')}
                  bg={sidebarMode === 'hover' ? 'gray.100' : 'transparent'}
                >
                  <HStack gap={2} flex={1}>
                    <Box w="16px" h="16px" display="flex" alignItems="center" justifyContent="center">
                      {sidebarMode === 'hover' && <Box w="6px" h="6px" bg="brand.500" borderRadius="full" />}
                    </Box>
                    <Text fontSize="sm">Expand on hover</Text>
                  </HStack>
                </MenuItem>
                <MenuItem
                  value="expanded"
                  onClick={() => setSidebarMode('expanded')}
                  bg={sidebarMode === 'expanded' ? 'gray.100' : 'transparent'}
                >
                  <HStack gap={2} flex={1}>
                    <Box w="16px" h="16px" display="flex" alignItems="center" justifyContent="center">
                      {sidebarMode === 'expanded' && <Box w="6px" h="6px" bg="brand.500" borderRadius="full" />}
                    </Box>
                    <Text fontSize="sm">Expanded</Text>
                  </HStack>
                </MenuItem>
                <MenuItem
                  value="collapsed"
                  onClick={() => setSidebarMode('collapsed')}
                  bg={sidebarMode === 'collapsed' ? 'gray.100' : 'transparent'}
                >
                  <HStack gap={2} flex={1}>
                    <Box w="16px" h="16px" display="flex" alignItems="center" justifyContent="center">
                      {sidebarMode === 'collapsed' && <Box w="6px" h="6px" bg="brand.500" borderRadius="full" />}
                    </Box>
                    <Text fontSize="sm">Collapsed</Text>
                  </HStack>
                </MenuItem>
              </MenuContent>
            </MenuPositioner>
          </Portal>
        </MenuRoot>
      </Box>
    </Box>
  )
}
